// Copyright ©️ 2026 eVolve MEP, LLC

import { createContext, useEffect, useCallback, useState, type ReactNode, useMemo } from 'react';

import {
  type SpoolRequestId,
  type HexTriplet,
  type AutodeskCloudModel,
  useEvolvePaginatedGetAll,
  FLAT_BLACK_FILLER,
  useEvolvePost,
  useEvolveDelete,
  type CreateSpoolRequestRequest,
  useEvolvePatch,
  type UpdateSpoolRequestRequest,
  type GetPaginatedAllOpts,
} from '@evolvemep/api-ts';

import { createColorScheme, GOLDEN_ANGLE } from 'helpers/createColorScheme';
import { isNil } from 'helpers/isNotNil';
import { strongKeys } from 'helpers/strongEntries';
import { useGeneralContext } from 'helpers/useGeneralContext';
import { useHasAccess } from 'hooks/user/useHasAccess';
import { type SpoolRequestEntry, useSpoolRequestsLoader } from 'modules/Shop/Spooling/Assigning/useSpoolRequestsLoader';

import { useSpoolColorScheme, type ColorSchemeOptions } from './useSpoolColorScheme';

const COLOR_SCHEME_ARGS: [number, number] = [(GOLDEN_ANGLE * 2) / 3, 1];
const requestColorSchemeOptions: ColorSchemeOptions<SpoolRequestEntry, SpoolRequestId> = {
  colorSchemeArgs: COLOR_SCHEME_ARGS,
  getIdentifier: ([id]) => id,
};

type SpoolRequestViewerStateContextType = {
  spoolRequests: SpoolRequestEntry[] | null;
  hasSpoolingAccess: boolean;
  colorScheme: Record<SpoolRequestId, HexTriplet>;
  visibleSpoolRequests: SpoolRequestId[];
  toggleSpoolRequestVisibility: (requestId: SpoolRequestId) => void;
  toggleAllVisible: () => void;
  updateSpoolColor: (requestId: SpoolRequestId, color: HexTriplet) => void;
  addRequest: (request: CreateSpoolRequestRequest) => Promise<void>;
  updateRequest: (id: SpoolRequestId, change: UpdateSpoolRequestRequest) => Promise<void>;
  removeRequest: (id: SpoolRequestId) => Promise<void>;
  loadingSpoolRequests: boolean;
  isMutationRunning: boolean;
};

const SpoolRequestsViewerStateContext = createContext<SpoolRequestViewerStateContextType | undefined>(undefined);

const spoolRequestComp = ([, { name: a }]: SpoolRequestEntry, [, { name: b }]: SpoolRequestEntry) => a.localeCompare(b);

export const SpoolRequestsViewerStateProvider = ({
  children,
  model,
}: {
  children: ReactNode;
  model: AutodeskCloudModel | null;
}) => {
  const [spoolRequests, setSpoolRequests] = useState<SpoolRequestViewerStateContextType['spoolRequests']>(null);

  const hasSpoolingAccess = useHasAccess('spoolCreation', 'write');

  const [visibleSpoolRequests, setVisibleSpoolRequests] = useState<
    SpoolRequestViewerStateContextType['visibleSpoolRequests']
  >([]);

  const {
    colorScheme,
    initializeColorScheme,
    updateColor: updateSpoolColor,
    setColorScheme,
  } = useSpoolColorScheme(requestColorSchemeOptions);

  // region initial load effects
  const fetchSpoolRequestsOpts = useMemo<GetPaginatedAllOpts<'shop/spool/request'>>(
    () => ({
      lazy: !hasSpoolingAccess || isNil(model),
      defaultConfig: {
        params: {
          autodeskCloudModelId: encodeURIComponent(model?.urn ?? ''),
          spoolRequestStatusTypeName: 'pending',
          orderBy: 'name:asc',
        },
      },
    }),
    [hasSpoolingAccess, model],
  );
  const {
    loading: loadingSpoolRequests,
    setDefaultOpts,
    reset,
    // The requests received directly from the API. Not exposed to the context.
    data: apiSpoolRequests,
  } = useEvolvePaginatedGetAll('shop/spool/request', fetchSpoolRequestsOpts);
  const loadSpoolRequestsFromModel = useSpoolRequestsLoader();
  useEffect(() => {
    // If the model changed, we should always clear the requested & visible spools.
    setVisibleSpoolRequests([]);
    initializeColorScheme([]);
    setDefaultOpts(fetchSpoolRequestsOpts);
    // Whenever we're about to change models, blank out `apiSpoolRequests` by calling `reset`.
    return () => reset();
  }, [reset, initializeColorScheme, fetchSpoolRequestsOpts, setDefaultOpts]);

  useEffect(() => {
    if (isNil(apiSpoolRequests)) return;

    void loadSpoolRequestsFromModel?.(apiSpoolRequests).then((requestsFromViewer) => {
      setVisibleSpoolRequests([]);
      initializeColorScheme(requestsFromViewer);
      setSpoolRequests(requestsFromViewer.toSorted(spoolRequestComp));
    });
  }, [apiSpoolRequests, loadSpoolRequestsFromModel, initializeColorScheme]);
  //endregion initial load effects

  // These mutator helpers dispatch REST api requests, then update the state in the React context.
  // region mutators
  const { apiCall: addRequestApiCall, loading: isAdding } = useEvolvePost('shop/spool/request');
  const addRequest = useCallback<SpoolRequestViewerStateContextType['addRequest']>(
    async (request) => {
      if (isNil(loadSpoolRequestsFromModel)) throw new Error('Cannot add spool request without a loaded model.');
      const response = await addRequestApiCall(request);
      const requestsFromViewer = await loadSpoolRequestsFromModel([response]);
      setSpoolRequests((prev) =>
        prev ? [...prev, ...requestsFromViewer].toSorted(spoolRequestComp) : requestsFromViewer,
      );
      setColorScheme((c) => {
        const sizeOfC = strongKeys(c).length;
        const newScheme = createColorScheme(sizeOfC + 1, ...COLOR_SCHEME_ARGS);
        return {
          ...c,
          [response.spoolRequestId]: newScheme[sizeOfC] ?? FLAT_BLACK_FILLER,
        };
      });
      setVisibleSpoolRequests((s) => [...s, response.spoolRequestId]);
    },
    [addRequestApiCall, setColorScheme, loadSpoolRequestsFromModel],
  );

  const { apiCall: updateRequestApiCall, loading: isUpdating } = useEvolvePatch(`shop/spool/request/:spoolRequestId`);
  const updateRequest = useCallback<SpoolRequestViewerStateContextType['updateRequest']>(
    async (id, change) => {
      await updateRequestApiCall(change, { url: `shop/spool/request/${id}` });
      setSpoolRequests((prev) => {
        if (isNil(prev)) return null;
        const updated: SpoolRequestEntry[] = prev.map(([otherId, spool]) =>
          otherId === id ? [id, { ...spool, ...change }] : [otherId, spool],
        );
        return updated.toSorted(spoolRequestComp);
      });
    },
    [updateRequestApiCall],
  );

  const { apiCall: removeRequestApiCall, loading: isRemoving } = useEvolveDelete('shop/spool/request/:spoolRequestId');
  const removeRequest = useCallback<SpoolRequestViewerStateContextType['removeRequest']>(
    async (id) => {
      await removeRequestApiCall({ url: `shop/spool/request/${id}` });
      setSpoolRequests((prev) => prev?.filter(([otherId]) => otherId !== id) ?? null);
      setColorScheme(({ [id]: _, ...rest }) => rest);
      setVisibleSpoolRequests((s) => s.filter((spoolId) => spoolId !== id));
    },
    [removeRequestApiCall, setColorScheme],
  );
  // endregion mutators

  const toggleSpoolRequestVisibility = useCallback<SpoolRequestViewerStateContextType['toggleSpoolRequestVisibility']>(
    (requestId) => {
      setVisibleSpoolRequests((s) => (s.includes(requestId) ? s.filter((id) => id !== requestId) : [...s, requestId]));
    },
    [],
  );

  const toggleAllVisible = useCallback<SpoolRequestViewerStateContextType['toggleAllVisible']>(() => {
    setVisibleSpoolRequests((s) => (s.length > 0 ? [] : (spoolRequests ?? []).map(([id]) => id)));
  }, [spoolRequests]);

  const value = {
    spoolRequests,
    hasSpoolingAccess,
    loadingSpoolRequests,
    colorScheme,
    visibleSpoolRequests,
    toggleSpoolRequestVisibility,
    toggleAllVisible,
    updateSpoolColor,
    addRequest,
    updateRequest,
    removeRequest,
    isMutationRunning: isAdding || isUpdating || isRemoving,
  } as const;

  return <SpoolRequestsViewerStateContext.Provider value={value}>{children}</SpoolRequestsViewerStateContext.Provider>;
};

export const useSpoolRequestsViewerState = () =>
  useGeneralContext(SpoolRequestsViewerStateContext, 'SpoolRequestsViewerStateContext');
